import { useCallback, useEffect, useRef, useState } from "react";
import { FaArrowLeft, FaArrowRight, FaShieldAlt, FaLock, FaKey, FaMobileAlt, FaExclamationTriangle, FaCheckCircle } from "react-icons/fa";
import "./CyberSecSlider.css";

// ─────────────────────────────────────────────────────────────────────────────
// BACKGROUND IMAGES — one per slide
// Using Unsplash Source URLs (free, no API key, CC0 licensed).
// Each is fixed at 1600×900 so browsers can cache a single copy.
// The CSS overlay darkens them to keep ZU brand colours dominant.
// ─────────────────────────────────────────────────────────────────────────────
// Slide 1 — dark shield / cyber lock art
const BG_INTRO    = "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1600&q=80&fit=crop";
// Slide 2 — hands on keyboard, threat metaphor
const BG_LOCK     = "https://images.unsplash.com/photo-1563206767-5b18f218e8de?w=1600&q=80&fit=crop";
// Slide 3 — abstract data / code screen (myth-busting)
const BG_MYTHS    = "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1600&q=80&fit=crop";
// Slide 4 — smartphone / authenticator app
const BG_MFA      = "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1600&q=80&fit=crop";
// Slide 5 — alert / warning / phone spam
const BG_FATIGUE  = "https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=1600&q=80&fit=crop";
// Slide 6 — checklist / action / person at laptop
const BG_CHECKLIST = "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1600&q=80&fit=crop";

// ─────────────────────────────────────────────────────────────────────────────
// EXPIRY CONFIG
// Week 1 content runs from 6 Oct 2026 to 13 Oct 2026.
// After the expiry date this component renders nothing — the hero reel
// simply skips it so Week 2 content can be dropped in without touching Hero.jsx.
// ─────────────────────────────────────────────────────────────────────────────
const EXPIRY_DATE = new Date("2026-10-13T23:59:59");

// How long each slide holds before auto-advancing (ms).
const SLIDE_DURATION = 7000;

// ─────────────────────────────────────────────────────────────────────────────
// WEEK 1 SLIDES  —  Passwords & Authentication
// Each slide is a focused, actionable awareness card.
// ─────────────────────────────────────────────────────────────────────────────
const CYBER_SLIDES = [
  // ── Intro / Campaign Hook ──────────────────────────────────────────────────
  {
    id: "cyber-intro",
    bgImage: BG_INTRO,
    week: "Week 1",
    theme: "Cybersecurity Awareness Month 2026",
    eyebrow: "Cyber Security Awareness Month",
    headline: "Don't Make It Easy for Them",
    body: "Cybercriminals don't need technical genius — they just need your weak password. This month, build habits that shut the door on them.",
    icon: FaShieldAlt,
    accent: "shield",
    cta: { label: "Read More", href: "/cyber-security-awareness" },
    stat: { value: "81%", label: "of breaches start with a stolen or weak password" },
  },

  // ── Slide 2: The password problem ─────────────────────────────────────────
  {
    id: "cyber-passwords",
    bgImage: BG_LOCK,
    week: "Week 1 · Passwords",
    theme: "Cybersecurity Awareness Month 2026",
    eyebrow: "The Threat",
    headline: "Your Password Is the Front Door. Is It Locked?",
    body: "\"abc123\", \"password\", your birthday — attackers try these first. Brute-force tools test millions of combinations per second. A weak password can be cracked before your next lecture.",
    icon: FaLock,
    accent: "lock",
    cta: { label: "Check Strength", href: "https://haveibeenpwned.com/Passwords" },
    table: [
      { weakness: "Short (< 8 chars)", example: "abc123", risk: "Cracked in seconds" },
      { weakness: "Common word", example: "password", risk: "Top of every attack list" },
      { weakness: "Personal info", example: "john1990", risk: "Guessable from social media" },
      { weakness: "Reused everywhere", example: "Same password", risk: "One breach = all accounts gone" },
    ],
  },

  // ── Slide 3: Password myth-busting ────────────────────────────────────────
  {
    id: "cyber-myths",
    bgImage: BG_MYTHS,
    week: "Week 1 · Passwords",
    theme: "Cybersecurity Awareness Month 2026",
    eyebrow: "Myth vs. Reality",
    headline: "Three Password Myths That Get People Hacked",
    body: "",
    icon: FaKey,
    accent: "key",
    cta: { label: "Use a Password Manager", href: "https://bitwarden.com" },
    myths: [
      {
        myth: "\"I have nothing worth stealing.\"",
        reality: "Every account is valuable — for spam, identity theft or resale. You don't need to be a CEO to be a target.",
      },
      {
        myth: "\"P@ssw0rd1 is strong because it has symbols.\"",
        reality: "Length beats complexity. \"correct-horse-battery-staple\" (28 chars) is far stronger than \"P@ss1\".",
      },
      {
        myth: "\"I'll know if my account is hacked.\"",
        reality: "Breaches go undetected for months. Check haveibeenpwned.com regularly.",
      },
    ],
  },

  // ── Slide 4: MFA intro ─────────────────────────────────────────────────────
  {
    id: "cyber-mfa-intro",
    bgImage: BG_MFA,
    week: "Week 1 · MFA",
    theme: "Cybersecurity Awareness Month 2026",
    eyebrow: "Your Second Lock",
    headline: "Multi-Factor Authentication Blocks 99% of Attacks",
    body: "Even if an attacker steals your password, MFA stops them cold. Microsoft data shows MFA blocks over 99% of automated account takeovers. Enable it on every account that offers it — right now.",
    icon: FaMobileAlt,
    accent: "mobile",
    cta: { label: "Enable MFA", href: "https://support.microsoft.com/en-us/account-billing/how-to-use-two-step-verification-with-your-microsoft-account-c7910146-672f-01e9-50a0-93b4585e7eb4" },
    mfaTypes: [
      { method: "Hardware key (FIDO2)", level: "Highest", note: "Phishing-resistant physical device" },
      { method: "Passkey (phone/laptop)", level: "Highest", note: "Tied to device biometrics" },
      { method: "Authenticator app", level: "High", note: "Microsoft / Google Authenticator" },
      { method: "SMS code", level: "Medium", note: "Better than nothing, but swappable" },
    ],
  },

  // ── Slide 5: MFA fatigue warning ──────────────────────────────────────────
  {
    id: "cyber-mfa-fatigue",
    bgImage: BG_FATIGUE,
    week: "Week 1 · MFA",
    theme: "Cybersecurity Awareness Month 2026",
    eyebrow: "Watch Out",
    headline: "MFA Fatigue: When Attackers Spam Your Phone",
    body: "Attackers who already have your password send wave after wave of push notifications hoping you approve one out of frustration. Don't.",
    icon: FaExclamationTriangle,
    accent: "warning",
    cta: { label: "Report to ICT", href: "mailto:itsupport@zetech.ac.ke?subject=Unexpected%20MFA%20Prompt" },
    tips: [
      "Never approve an MFA prompt you did NOT initiate.",
      "Receiving unexpected prompts means your password is already compromised — change it immediately.",
      "Use number-matching MFA where available (enter a code from the screen, not just 'approve').",
      "Report unexpected MFA prompts to itsupport@zetech.ac.ke as a security incident.",
    ],
  },

  // ── Slide 6: Action checklist ──────────────────────────────────────────────
  {
    id: "cyber-checklist",
    bgImage: BG_CHECKLIST,
    week: "Week 1 · Take Action",
    theme: "Cybersecurity Awareness Month 2026",
    eyebrow: "Your Week 1 Checklist",
    headline: "Five Steps. Do Them Today.",
    body: "Small changes made now prevent major headaches later. Check each one off before the week ends.",
    icon: FaCheckCircle,
    accent: "check",
    cta: { label: "ZU ICT Support", href: "mailto:itsupport@zetech.ac.ke" },
    checklist: [
      "Change any weak or reused passwords to long, unique passphrases (16+ characters).",
      "Install a password manager (Bitwarden is free and open-source).",
      "Enable MFA on your Zetech student portal, email, and social accounts.",
      "Check haveibeenpwned.com to see if your email has been in a breach.",
      "Report any suspicious login attempts or unexpected MFA prompts to itsupport@zetech.ac.ke immediately.",
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

// Detect mobile viewport so we can limit the amount of content rendered
// inside the fixed-height hero. Content-heavy slides (table, myths, MFA)
// are trimmed on small screens and the user is directed to the full article.
function useMobileBreakpoint(px = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < px : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${px - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [px]);
  return isMobile;
}
export default function CyberSecSlider({ onCycleEnd, onRequestVideo }) {
  // Hard expiry — returns null (no render) after expiry date.
  const isExpired = Date.now() > EXPIRY_DATE.getTime();
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const isMobile = useMobileBreakpoint(768);

  const onCycleEndRef = useRef(onCycleEnd);
  const onRequestVideoRef = useRef(onRequestVideo);
  useEffect(() => { onCycleEndRef.current = onCycleEnd; }, [onCycleEnd]);
  useEffect(() => { onRequestVideoRef.current = onRequestVideo; }, [onRequestVideo]);

  const hoverCapable = useRef(
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: hover)").matches
  );

  const reducedMotion = useRef(
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  const nextSlide = useCallback(() => {
    setCurrent((prev) => (prev + 1) % CYBER_SLIDES.length);
  }, []);

  const prevSlide = useCallback(() => {
    if (current === 0 && onRequestVideoRef.current) {
      onRequestVideoRef.current();
      return;
    }
    setCurrent((prev) => (prev - 1 + CYBER_SLIDES.length) % CYBER_SLIDES.length);
  }, [current]);

  // Touch / swipe
  const touchStart = useRef(null);
  const handleTouchStart = (e) => { touchStart.current = e.touches?.[0]?.clientX ?? null; };
  const handleTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const delta = (e.changedTouches?.[0]?.clientX ?? touchStart.current) - touchStart.current;
    touchStart.current = null;
    if (Math.abs(delta) < 45) return;
    if (delta < 0) nextSlide();
    else prevSlide();
  };

  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [nextSlide, prevSlide]);

  // Auto-advance
  useEffect(() => {
    if (paused || isExpired) return undefined;
    const timer = setInterval(() => {
      setCurrent((prev) => {
        if (prev >= CYBER_SLIDES.length - 1) return prev;
        return prev + 1;
      });
    }, SLIDE_DURATION);
    return () => clearInterval(timer);
  }, [paused, isExpired]);

  // Finished last slide → hand off to HeroSlider
  useEffect(() => {
    if (paused || current < CYBER_SLIDES.length - 1 || isExpired) return undefined;
    const timer = setTimeout(() => {
      if (onCycleEndRef.current) onCycleEndRef.current();
    }, SLIDE_DURATION);
    return () => clearTimeout(timer);
  }, [current, paused, isExpired]);

  // Expired — render nothing; Hero.jsx skips straight to HeroSlider
  if (isExpired) return null;

  const slide = CYBER_SLIDES[current];
  const Icon = slide.icon;

  return (
    <section
      className={`cyber-slider cyber-slider--${slide.accent}`}
      onMouseEnter={() => { if (hoverCapable.current) setPaused(true); }}
      onMouseLeave={() => { if (hoverCapable.current) setPaused(false); }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-roledescription="carousel"
      aria-label="Cybersecurity Awareness Month 2026"
      data-reduced-motion={reducedMotion.current ? "true" : "false"}
    >
      {/* Background — full-bleed photo + brand overlay + circuit pattern */}
      <div className="cyber-bg" aria-hidden="true">
        {/* The photo — fades in on mount via CSS, Ken-Burns-free (still image) */}
        <img
          key={slide.bgImage}
          src={slide.bgImage}
          alt=""
          className="cyber-bg-photo"
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
        {/* Brand-tinted gradient scrim over the photo */}
        <div className="cyber-bg-scrim" />
        {/* Subtle SVG circuit overlay — keeps brand feel */}
        <div className="cyber-bg-grid" />
        {/* Accent colour radial glow */}
        <div className="cyber-glow cyber-glow--1" />
        <div className="cyber-glow cyber-glow--2" />
      </div>

      {/* Binary / hex rain strip — decorative */}
      <div className="cyber-rain" aria-hidden="true">
        {["01", "FF", "A3", "7E", "C1", "0D", "B9", "22", "5A", "F4"].map((v, i) => (
          <span key={i} className="cyber-rain__char" style={{ "--i": i }}>
            {v}
          </span>
        ))}
      </div>

      {/* Main content */}
      <div className="cyber-overlay">
        <div className="cyber-wrapper">

          {/* LEFT: campaign identity */}
          <div className="cyber-identity">
            <div className="cyber-shield-wrap" aria-hidden="true">
              <Icon className="cyber-icon" />
            </div>
            <p className="cyber-campaign">{slide.theme}</p>
            <span className="cyber-week-badge">{slide.week}</span>
          </div>

          {/* RIGHT: slide content */}
          <div className="cyber-content">
            <span className="cyber-eyebrow">{slide.eyebrow}</span>
            <h1 className="cyber-headline">{slide.headline}</h1>

            {slide.body && <p className="cyber-body">{slide.body}</p>}

            {/* Password weakness table — on mobile show only first 2 rows */}
            {slide.table && (
              <div className="cyber-table-wrap">
                <table className="cyber-table">
                  <thead>
                    <tr>
                      <th>Weakness</th>
                      <th>Example</th>
                      <th>Why It Fails</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(isMobile ? slide.table.slice(0, 2) : slide.table).map((row) => (
                      <tr key={row.weakness}>
                        <td>{row.weakness}</td>
                        <td><code>{row.example}</code></td>
                        <td>{row.risk}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Myth vs reality — on mobile show only first 2 */}
            {slide.myths && (
              <ul className="cyber-myths">
                {(isMobile ? slide.myths.slice(0, 2) : slide.myths).map((m) => (
                  <li key={m.myth} className="cyber-myth-item">
                    <span className="cyber-myth-label">MYTH</span>
                    <span className="cyber-myth-text">{m.myth}</span>
                    <span className="cyber-reality-label">REALITY</span>
                    <span className="cyber-reality-text">{m.reality}</span>
                  </li>
                ))}
              </ul>
            )}

            {/* MFA methods — on mobile show only first 2 */}
            {slide.mfaTypes && (
              <ul className="cyber-mfa-list">
                {(isMobile ? slide.mfaTypes.slice(0, 2) : slide.mfaTypes).map((m) => (
                  <li key={m.method} className={`cyber-mfa-item cyber-mfa-item--${m.level.toLowerCase().replace(/\s/g, "-")}`}>
                    <strong>{m.method}</strong>
                    <span className="cyber-mfa-level">{m.level}</span>
                    <span className="cyber-mfa-note">{m.note}</span>
                  </li>
                ))}
              </ul>
            )}

            {/* Warning tips — on mobile show first 3 */}
            {slide.tips && (
              <ul className="cyber-tips">
                {(isMobile ? slide.tips.slice(0, 3) : slide.tips).map((tip, i) => (
                  <li key={i} className="cyber-tip-item">
                    <FaExclamationTriangle className="cyber-tip-icon" aria-hidden="true" />
                    {tip}
                  </li>
                ))}
              </ul>
            )}

            {/* Action checklist — on mobile show first 3 */}
            {slide.checklist && (
              <ol className="cyber-checklist">
                {(isMobile ? slide.checklist.slice(0, 3) : slide.checklist).map((item, i) => (
                  <li key={i} className="cyber-checklist-item">
                    <FaCheckCircle className="cyber-check-icon" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ol>
            )}

            {/* Stat callout */}
            {slide.stat && (
              <div className="cyber-stat">
                <span className="cyber-stat-value">{slide.stat.value}</span>
                <span className="cyber-stat-label">{slide.stat.label}</span>
              </div>
            )}

            {/* CTA */}
            {slide.cta && (
              <a
                href={slide.cta.href}
                className="cyber-cta"
                target={slide.cta.href.startsWith("http") ? "_blank" : undefined}
                rel={slide.cta.href.startsWith("http") ? "noopener noreferrer" : undefined}
              >
                {isMobile ? "Read Full Article →" : slide.cta.label}
              </a>
            )}
          </div>

        </div>
      </div>

      {/* Arrows */}
      <button
        className="hero-arrow1 hero-arrow-left1 cyber-arrow"
        onClick={prevSlide}
        aria-label="Previous slide"
      >
        <FaArrowLeft />
      </button>
      <button
        className="hero-arrow1 hero-arrow-right1 cyber-arrow"
        onClick={nextSlide}
        aria-label="Next slide"
      >
        <FaArrowRight />
      </button>

      {/* Indicators */}
      <div className="hero-indicators cyber-indicators" role="tablist" aria-label="Choose a cybersecurity awareness slide">
        {onRequestVideo && (
          <button
            type="button"
            className="indicator indicator--video"
            onClick={() => onRequestVideo()}
            aria-label="Play the intro video"
            title="Intro video"
          />
        )}
        {CYBER_SLIDES.map((s, i) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={current === i}
            className={`indicator ${current === i ? "active" : ""}`}
            onClick={() => setCurrent(i)}
            aria-label={`Cybersecurity slide: ${s.eyebrow}`}
            title={s.eyebrow}
          >
            {current === i && !paused && (
              <span className="indicator-progress" key={`cp-${i}`} style={{ animationDuration: `${SLIDE_DURATION}ms` }} />
            )}
          </button>
        ))}
      </div>

      {/* Screen-reader status */}
      <p className="hero-sr-status" aria-live="polite">
        {`Cybersecurity slide ${current + 1} of ${CYBER_SLIDES.length}: ${slide.headline}`}
      </p>

      {/* Expiry notice (visible until the last day) */}
      <div className="cyber-expiry" aria-label="Campaign schedule">
        <span>⏱ Week 1 ends 13 Oct 2026 · New content drops weekly</span>
      </div>
    </section>
  );
}
